import { computed, isSignal, linkedSignal, Resource, Signal } from '@angular/core';
import { ReadonlyCollectionRepository } from '../../repositories/abstraction';
import { cancelableResource } from '../../signals/cancelable-resource';
import { ReadonlyCollectionService } from './abstraction';

export interface RepositoryCollectionServiceOptions<T extends object> {
  defaultItems?: T[];
  skipInitialLoad?: boolean;
}

export abstract class ReadonlyRepositoryCollectionService<
    T extends object,
    TResult,
    TLoadParams,
  >
  implements Resource<T[]>, ReadonlyCollectionService<T>
{
  abstract params: Signal<TLoadParams>;

  protected _resource = cancelableResource({
    skipInitialLoad: this.options?.skipInitialLoad ?? false,
    params: () => ({
      params: this.params(),
    }),
    loader: async (param) => {
      return await this.repository.load(param.params.params);
    },
  });

  resource = this._resource.asReadonly();

  all = linkedSignal(() => {
    //TODO: use hasValue method here to prevent endless loop
    if (this._resource.status() === 'error') {
      return this.options?.defaultItems ?? [];
    }
    return this.getAll(this._resource.value());
  });

  allIds = computed(() => this.all().map((o) => this.getId(o)));
  allDict = computed(() => {
    const dict: Record<string | number, T> = {};
    for (const item of this.all()) dict[this.getId(item)] = item;
    return dict;
  });

  ids = computed(() => this.all().map((o) => this.getId(o)));
  dict = computed(() => {
    const dict: Record<string | number, T> = {};
    for (const item of this.all()) dict[this.getId(item)] = item;
    return dict;
  });

  item = (
    id: string | number | null | Signal<string | number | null>
  ): Signal<T | null> => {
    return computed(() => {
      if (isSignal(id)) id = id();
      return this.all().find((o) => id === this.getId(o)) ?? null;
    });
  };

  isLoading = this._resource.isLoading;
  error = this._resource.error;
  value = this.all.asReadonly();
  status = this.resource.status;

  protected constructor(
    protected repository: ReadonlyCollectionRepository<TResult, TLoadParams>,
    protected options?: RepositoryCollectionServiceOptions<T>
  ) {
    if (options?.defaultItems) this.all.set(options.defaultItems);
  }

  load(): boolean {
    if (this._resource.status() === 'idle') return this._resource.reload();
    return false;
  }

  reload(): boolean {
    return this._resource.reload();
  }

  cancel(): void {
    this._resource.cancel();
  }

  clear(): void {
    this._resource.set(undefined);
  }

  reset(): void {
    this._resource.set(undefined);
    this.all.set(this.options?.defaultItems ?? []);
  }

  hasValue(): this is Resource<Exclude<T[], undefined>> {
    return !!this._resource.hasValue();
  }

  protected getId = (item: T): string | number => {
    return item['id'];
  };

  protected abstract getAll(result: TResult | null | undefined): T[];
}
